import React, { useEffect, useState, useMemo } from "react";
import { useParams,useNavigate  } from "react-router-dom";
import { supabase } from "../supabaseClient";


function CustomerDetails({ user }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const [blockDistributions, setBlockDistributions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [sales, setSales] = useState([]);
  const [saleDetails, setSaleDetails] = useState([]);
  const [tab, setTab] = useState("account");

  // states for filter in blocks tab
  const [filterType, setFilterType] = useState("material");
  const [filterValue, setFilterValue] = useState("");

  // states for edit/delete popups
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [paymentEditId, setPaymentEditId] = useState(null);
  const [newPayment, setNewPayment] = useState({
    amount: "",
    payment_date: new Date().toISOString().slice(0, 10),
    reference: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  async function fetchData() {
    // customer
    const { data: custData } = await supabase
      .from("customer")
      .select("*")
      .eq("id", id)
      .single();
    setCustomer(custData);

    // materials
    const { data: mats } = await supabase.from("material").select("*");
    setMaterials(mats || []);

    // showrooms
    const { data: showroomData } = await supabase.from("showroom").select("*");
    setShowrooms(showroomData || []);

    // block distributions
    const { data: blockDistData } = await supabase
      .from("block_distribution")
      .select("*");
    setBlockDistributions(blockDistData || []);

    // supplier blocks (only if roles include "مورد")
    if (
      custData &&
      (Array.isArray(custData.roles)
        ? custData.roles.includes("مورد")
        : (custData.roles || "").includes("مورد"))
    ) {
      const { data: blocksData } = await supabase
        .from("block")
        .select("*")
        .eq("customer_id", id)
        .order("id", { ascending: false });
      setBlocks(blocksData || []);
    } else {
      setBlocks([]);
    }

    // payments
    const { data: paymentsData } = await supabase
      .from("payment")
      .select("*")
      .eq("customer_id", id)
      .order("payment_date", { ascending: false });
    setPayments(paymentsData || []);

    // sales (NOTE: includes discount_value)
    const { data: salesData } = await supabase
      .from("sale")
      .select("*")
      .eq("customer_id", id)
      .order("date", { ascending: false });
    setSales(salesData || []);

    // sale details for those sales
    const saleIds = (salesData || []).map((s) => s.id);
    let detailsData = [];
    if (saleIds.length) {
      const { data: saleDetailsData } = await supabase
        .from("sale_details")
        .select("*")
        .in("sale_id", saleIds);
      detailsData = saleDetailsData || [];
    }
    setSaleDetails(detailsData);
  }

  // is supplier?
  const isSupplier = Array.isArray(customer?.roles)
    ? customer.roles.includes("مورد")
    : (customer?.roles || "").includes("مورد");

  // lookup helpers
  function getMaterialName(mid) {
    if (!mid) return "-";
    const mat = materials.find((m) => m.id === mid);
    return mat ? mat.material_name || mat.name : "-";
  }

  function getBlockShowrooms(blockId) {
    const distros = blockDistributions.filter(
      (d) => d.block_id === blockId && d.showroom_id
    );
    if (!distros.length) return "-";
    return distros
      .map((d) => {
        const s = showrooms.find((x) => x.id === d.showroom_id);
        let name = s ? s.name : `Showroom ${d.showroom_id}`;
        if (d.slabs_count) {
          name += ` (${d.slabs_count} slab)`;
        }
        return name;
      })
      .join(" / ");
  }

  // ===== الحساب بصافي بعد الخصم =====
  // 1) subtotal لكل فاتورة من تفاصيلها
  const saleSubtotalsById = useMemo(() => {
    const map = {};
    for (const d of saleDetails) {
      map[d.sale_id] = (map[d.sale_id] || 0) + Number(d.total_price || 0);
    }
    return map;
  }, [saleDetails]);

  // 2) إجمالي المبيعات بعد الخصم (discount_value على مستوى الفاتورة)
  const totalSalesNet = useMemo(() => {
    return sales.reduce((sum, s) => {
      const subtotal = saleSubtotalsById[s.id] || 0;
      const discount = Number(s.discount_value || 0);
      const net = Math.max(0, subtotal - discount);
      return sum + net;
    }, 0);
  }, [sales, saleSubtotalsById]);

  // المدفوعات
  const totalPayments = useMemo(
    () => payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [payments]
  );

  // المتبقي = صافي المبيعات - المدفوعات
  const balance = useMemo(
    () => totalSalesNet - totalPayments,
    [totalSalesNet, totalPayments]
  );

  // payments CRUD
  async function handlePaymentSubmit(e) {
    e.preventDefault();
    if (!newPayment.amount) return alert("Enter a payment amount");
    if (paymentEditId) {
      await supabase
        .from("payment")
        .update({ ...newPayment, amount: Number(newPayment.amount) })
        .eq("id", paymentEditId);
    } else {
      await supabase.from("payment").insert([
        {
          customer_id: customer.id,
          amount: Number(newPayment.amount),
          payment_date: newPayment.payment_date,
          reference: newPayment.reference,
          notes: newPayment.notes,
        },
      ]);
    }
    setShowPaymentPopup(false);
    setPaymentEditId(null);
    fetchData();
  }

  function handleEditPayment(payment) {
    setNewPayment({
      amount: payment.amount,
      payment_date: payment.payment_date,
      reference: payment.reference || "",
      notes: payment.notes || "",
    });
    setPaymentEditId(payment.id);
    setShowPaymentPopup(true);
  }

  async function handleDeletePayment(id) {
    if (window.confirm("Delete this payment?")) {
      await supabase.from("payment").delete().eq("id", id);
      fetchData();
    }
  }

  const blockLookup = Object.fromEntries(blocks.map((b) => [b.id, b.code]));
  const showroomLookup = Object.fromEntries(showrooms.map((s) => [s.id, s.name]));

  const filteredBlocks = blocks.filter((b) => {
    if (!filterValue) return true;
    if (filterType === "material") return b.material_id === Number(filterValue);
    if (filterType === "code")
      return b.code.toLowerCase().includes(filterValue.toLowerCase());
    return true;
  });

  // ===== UI =====
  return (
    <div className="ui-legacy-page" dir="ltr" style={{ maxWidth: 1100, margin: "auto", padding: 30 }}>
      <h2>
        Details {isSupplier ? "Supplier" : "Customer"}:{" "}
        <span style={{ color: "var(--accent, #514536)" }}>{customer?.name}</span>
      </h2>

      <div className="ui-actions" style={{ display: "flex", gap: 10, marginBottom: 24 }}>
        <button className="ui-secondary"
          aria-pressed={tab === "account"}
          onClick={() => setTab("account")}
          style={{ fontWeight: tab === "account" ? "bold" : "normal" }}
        >
          Statement
        </button>
        <button className="ui-secondary"
          aria-pressed={tab === "payments"}
          onClick={() => setTab("payments")}
          style={{ fontWeight: tab === "payments" ? "bold" : "normal" }}
        >
          Payments
        </button>
        <button className="ui-secondary"
          aria-pressed={tab === "sales"}
          onClick={() => setTab("sales")}
          style={{ fontWeight: tab === "sales" ? "bold" : "normal" }}
        >
          Sales
        </button>
        {isSupplier && (
          <button className="ui-secondary"
            aria-pressed={tab === "blocks"}
            onClick={() => setTab("blocks")}
            style={{ fontWeight: tab === "blocks" ? "bold" : "normal" }}
          >
            Blocks
          </button>
        )}
      </div>

      {/* كشف الحساب */}
      {tab === "account" && (
        <div>
          <h3>Account statement</h3>
          <div style={{ fontSize: 18, margin: "12px 0" }}>
            <b>Total sales (after discount):</b>{" "}
            {totalSalesNet.toLocaleString()} EGP
            <br />
            <b>Total payments:</b> {totalPayments.toLocaleString()} EGP
            <br />
          <b>
  {balance < 0 ? (
    <>
      Customer credit balance{" "}
      <span style={{ color: "#514536" }}>
        {Math.abs(balance).toLocaleString()}
      </span>{" "}
      EGP
    </>
  ) : balance > 0 ? (
    <>
      Customer balance due{" "}
      <span style={{ color: "#514536" }}>
        {balance.toLocaleString()}
      </span>{" "}
      EGP
    </>
  ) : (
    <>This customer has no outstanding balance</>
  )}
</b>
          </div>
        </div>
      )}

      {/* المدفوعات */}
      {tab === "payments" && (
        <div>
          <h3>Payments</h3>
         
          <div className="ui-table-scroll"><table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              direction: "ltr",
            }}
          >
            <thead>
              <tr>
                <th>No.</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Reference</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, i) => (
                <tr key={p.id}>
                  <td>{i + 1}</td>
                  <td>{p.payment_date}</td>
                  <td>{p.amount}</td>
                  <td>{p.reference || "-"}</td>
                  <td>{p.notes || "-"}</td>
                  <td><div className="ui-actions">
                    <button className="ui-secondary" onClick={() => handleEditPayment(p)}>Edit</button>
                    <button className="ui-secondary"
                      style={{ color: "#514536", marginRight: 5 }}
                      onClick={() => handleDeletePayment(p.id)}
                    >
                      Delete
                    </button>
                  </div></td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ color: "#716759", textAlign: "center" }}>
                    No payments
                  </td>
                </tr>
              )}
            </tbody>
          </table></div>

          {/* Popup */}
          {showPaymentPopup && (
            <div className="ui-backdrop"
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(81,69,54,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
            >
              <div className="ui-modal ui-modal-small"
                style={{
                  background: "#f2ede4",
                  borderRadius: 12,
                  padding: 24,
                  minWidth: 320,
                  boxShadow: "0 0 12px #51453633",
                  textAlign: "left",
                  direction: "ltr",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 20 }}>
                  {paymentEditId ? "Edit payment" : "Add payment"}
                </h3>
                <form className="ui-form" onSubmit={handlePaymentSubmit}>
                  <div className="ui-field" style={{ marginBottom: 12 }}>
                    <label>Amount:</label>
                    <input
                      type="number"
                      required
                      min={1}
                      placeholder="Amount"
                      value={newPayment.amount}
                      onChange={(e) =>
                        setNewPayment((f) => ({ ...f, amount: e.target.value }))
                      }
                      dir="ltr"
                      style={{ width: "100%", textAlign: "left" }}
                    />
                  </div>
                  <div className="ui-field" style={{ marginBottom: 12 }}>
                    <label>Date:</label>
                    <input
                      type="date"
                      value={newPayment.payment_date}
                      onChange={(e) =>
                        setNewPayment((f) => ({
                          ...f,
                          payment_date: e.target.value,
                        }))
                      }
                      dir="ltr"
                      style={{ width: "100%", textAlign: "left" }}
                    />
                  </div>
                  <div className="ui-field" style={{ marginBottom: 12 }}>
                    <label>Reference:</label>
                    <input
                      placeholder="Transaction reference (optional)"
                      value={newPayment.reference}
                      onChange={(e) =>
                        setNewPayment((f) => ({
                          ...f,
                          reference: e.target.value,
                        }))
                      }
                      dir="ltr"
                      style={{ width: "100%", textAlign: "left" }}
                    />
                  </div>
                  <div className="ui-field" style={{ marginBottom: 18 }}>
                    <label>Notes:</label>
                    <input
                      placeholder="Notes (optional)"
                      value={newPayment.notes}
                      onChange={(e) =>
                        setNewPayment((f) => ({ ...f, notes: e.target.value }))
                      }
                      dir="ltr"
                      style={{ width: "100%", textAlign: "left" }}
                    />
                  </div>
                  <div className="ui-actions ui-form-actions" style={{ display: "flex", gap: 10 }}>
                    <button className="ui-secondary" type="submit" style={{ minWidth: 90 }}>
                      {paymentEditId ? "Refresh" : "Add"}
                    </button>
                    <button className="ui-secondary"
                      type="button"
                      style={{ background: "#d2cbc1", minWidth: 70 }}
                      onClick={() => {
                        setShowPaymentPopup(false);
                        setPaymentEditId(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* المبيعات */}
      {tab === "sales" && (
        <div>
          <h3>Sales</h3>
          <div className="ui-table-scroll"><table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 15,
              direction: "ltr",
            }}
          >
            <thead>
              <tr>
                <th>No.</th>
                <th>Date</th>
                <th>Code</th>
                <th>Showroom</th>
                <th>Table count</th>
                <th>Area(m²)</th>
                <th>Price per m²</th>
                <th>Total after discount</th>
              </tr>
            </thead>
            <tbody>
              {saleDetails
                .filter((d) => sales.some((s) => s.id === d.sale_id))
                .map((d, i) => (
                  <tr key={d.id || `${d.sale_id}-${i}`}>
                    <td>{i + 1}</td>
                    <td>
                      {sales.find((s) => s.id === d.sale_id)?.date || (
                        <span style={{ color: "#716759" }}>—</span>
                      )}
                    </td>
                    <td>{blockLookup[d.block_id] || d.block_id}</td>
                    <td>
                      {showroomLookup[
                        sales.find((s) => s.id === d.sale_id)?.showroom_id
                      ] || "-"}
                    </td>
                    <td>{d.slabs_sold || "-"}</td>
                    <td>{d.area_sold || "-"}</td>
                    <td>{d.price_per_m2 || "-"}</td>
                    <td>{totalSalesNet.toLocaleString() || "-"}</td> 
                  </tr>
                ))}
              {saleDetails.filter((d) => sales.some((s) => s.id === d.sale_id))
                .length === 0 && (
                <tr>
                  <td colSpan={8} style={{ color: "#716759", textAlign: "center" }}>
                    No sales recorded
                  </td>
                </tr>
              )}
            </tbody>
          </table></div>
        </div>
      )}

      {/* البلوكات */}
      {tab === "blocks" && isSupplier && (
        <div>
          <h3>Blocks for {customer?.name}</h3>

          <div className="ui-field" style={{ marginBottom: 12 }}>
            <label style={{ marginRight: 8 }}>Filter by:</label>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setFilterValue("");
              }}
              style={{ marginRight: 8 }}
            >
              <option value="material">Material</option>
              <option value="code">Block code</option>
            </select>

            {filterType === "material" && (
              <select
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
              >
                <option value="">All materials</option>
                {materials.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.material_name || m.name}
                  </option>
                ))}
              </select>
            )}

            {filterType === "code" && (
              <input
                type="text"
                placeholder="Enter a block code"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
              />
            )}
          </div>

          <div className="ui-table-scroll"><table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              direction: "ltr",
              textAlign: "left",
              fontSize: 15,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#d2cbc1" }}>
                <th style={thTdStyle}>#</th>
                <th style={thTdStyle}>Block code</th>
                <th style={thTdStyle}>Material</th>
                <th style={thTdStyle}>Width</th>
                <th style={thTdStyle}>Height</th>
                <th style={thTdStyle}>Showroom/Warehouse</th>
                <th style={thTdStyle}>Available slabs</th>
                <th style={thTdStyle}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlocks.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", color: "#716759" }}>
                    No blocks match these filters
                  </td>
                </tr>
              )}

              {filteredBlocks.map((block, idx) => {
                const distros = blockDistributions.filter(
                  (d) => d.block_id === block.id
                );
                const totalSlabs = distros.reduce(
                  (sum, d) => sum + Number(d.slabs_count || 0),
                  0
                );
                const showroomsNames = distros
                  .map((d) => {
                    const showroom = showrooms.find((s) => s.id === d.showroom_id);
                    return showroom
                      ? `${showroom.name} (${d.slabs_count} slabs)`
                      : `Showroom ${d.showroom_id} (${d.slabs_count} slabs)`;
                  })
                  .join(" / ");

                return (
                  <tr key={block.id} style={{ borderBottom: "1px solid #d2cbc1" }}>
                    <td style={tdStyle}>{idx + 1}</td>
                    <td style={tdStyle}>{block.code}</td>
                    <td style={tdStyle}>{getMaterialName(block.material_id)}</td>
                    <td style={tdStyle}>{block.width}</td>
                    <td style={tdStyle}>{block.height}</td>
                    <td style={tdStyle}>{showroomsNames || "-"}</td>
                    <td style={tdStyle}>{totalSlabs}</td>
                    <td style={tdStyle}>{block.notes || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}

const thTdStyle = {
  padding: 10,
  border: "1px solid #d2cbc1",
  fontWeight: "bold",
  textAlign: "left",
};

const tdStyle = {
  padding: 10,
  border: "1px solid #d2cbc1",
  textAlign: "left",
};

export default CustomerDetails;
